/**
 * Premium Report HTML Generator
 * 
 * Generates a visually stunning, modern, minimalist HTML report
 * designed to be emotionally impactful and shareable.
 */

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { FinancialSummary, ProjectReport, ClientReport, TimeReport } from './reports-engine';
import { formatCurrency } from './reports-engine';

export interface PremiumReportData {
  userName: string;
  period: { start: string; end: string };
  financial: FinancialSummary | null;
  projects: ProjectReport[] | null;
  clients: ClientReport[] | null;
  timeReport: TimeReport | null;
  previousPeriod?: {
    revenue: number;
    hourlyRate: number;
  };
}

export interface ReportInsight {
  type: 'positive' | 'negative' | 'neutral';
  text: string;
}

/**
 * Generate automatic insights based on report data
 */
export function generateInsights(data: PremiumReportData): ReportInsight[] {
  const insights: ReportInsight[] = [];
  
  if (!data.financial) return insights;
  
  const { financial, previousPeriod, clients, timeReport } = data;
  
  // Revenue growth insight
  if (previousPeriod && previousPeriod.revenue > 0) {
    const growth = ((financial.confirmedRevenue - previousPeriod.revenue) / previousPeriod.revenue) * 100;
    if (growth > 10) {
      insights.push({
        type: 'positive',
        text: `Excelente! Sua receita cresceu ${Math.round(growth)}% em relação ao período anterior.`
      });
    } else if (growth > 0) {
      insights.push({
        type: 'positive',
        text: `Sua receita teve um crescimento de ${Math.round(growth)}% comparado ao período anterior.`
      });
    } else if (growth < -10) {
      insights.push({
        type: 'negative',
        text: `Atenção: Sua receita caiu ${Math.abs(Math.round(growth))}% em relação ao período anterior.`
      });
    }
  }
  
  // Hourly rate insight
  if (previousPeriod && previousPeriod.hourlyRate > 0 && financial.realHourlyRate > 0) {
    const rateGrowth = ((financial.realHourlyRate - previousPeriod.hourlyRate) / previousPeriod.hourlyRate) * 100;
    if (rateGrowth > 5) {
      insights.push({
        type: 'positive',
        text: `Seu valor/hora cresceu ${Math.round(rateGrowth)}% comparado ao mês passado.`
      });
    }
  }
  
  // Billable percentage insight
  if (timeReport) {
    if (timeReport.billablePercentage >= 80) {
      insights.push({
        type: 'positive',
        text: `Ótima eficiência! ${timeReport.billablePercentage}% das suas horas foram faturáveis.`
      });
    } else if (timeReport.billablePercentage < 60) {
      const nonBillableHours = timeReport.nonBillableHours;
      const lostValue = nonBillableHours * financial.realHourlyRate;
      insights.push({
        type: 'negative',
        text: `${Math.round(100 - timeReport.billablePercentage)}% do seu tempo não foi faturável, representando ${formatCurrency(lostValue)} em potencial perdido.`
      });
    }
  }
  
  // Client concentration insight
  if (clients && clients.length > 0) {
    const topClient = clients[0];
    if (topClient.revenueShare > 60) {
      insights.push({
        type: 'neutral',
        text: `${topClient.name} representa ${topClient.revenueShare}% da sua receita. Considere diversificar sua carteira.`
      });
    }
  }
  
  return insights;
}

/**
 * Find the most profitable client
 */
export function getMostProfitableClient(clients: ClientReport[] | null): ClientReport | null {
  if (!clients || clients.length === 0) return null;
  return clients.reduce((max, client) => 
    client.totalRevenue > max.totalRevenue ? client : max
  , clients[0]);
}

/**
 * Find the most profitable project (highest effective rate)
 */
export function getMostProfitableProject(projects: ProjectReport[] | null): ProjectReport | null {
  if (!projects || projects.length === 0) return null;
  const withHours = projects.filter(p => p.hoursSpent > 0);
  if (withHours.length === 0) return null;
  return withHours.reduce((max, project) => 
    project.effectiveRate > max.effectiveRate ? project : max
  , withHours[0]);
}

/**
 * Calculate lost potential value from non-billable hours
 */
export function calculateLostPotential(
  timeReport: TimeReport | null,
  hourlyRate: number
): number {
  if (!timeReport) return 0;
  return timeReport.nonBillableHours * hourlyRate;
}

/**
 * Generate premium HTML report
 */
export function generatePremiumHTML(data: PremiumReportData): string {
  const insights = generateInsights(data);
  const topClient = getMostProfitableClient(data.clients);
  const topProject = getMostProfitableProject(data.projects);
  const lostPotential = calculateLostPotential(data.timeReport, data.financial?.realHourlyRate || 0);
  
  const growthPercent = data.previousPeriod && data.previousPeriod.revenue > 0
    ? ((data.financial?.confirmedRevenue || 0) - data.previousPeriod.revenue) / data.previousPeriod.revenue * 100
    : null;

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Relatório FreelaDash - ${data.period.start} a ${data.period.end}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
  <style>
    :root {
      --primary: #3b82f6;
      --primary-dark: #1e40af;
      --primary-gradient: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #0ea5e9 100%);
      --success: #10b981;
      --warning: #f59e0b;
      --danger: #ef4444;
      --text-primary: #0f172a;
      --text-secondary: #475569;
      --text-muted: #94a3b8;
      --bg-light: #f8fafc;
      --bg-card: #ffffff;
      --border: #e2e8f0;
    }
    
    * { margin: 0; padding: 0; box-sizing: border-box; }
    
    body {
      font-family: 'Inter', system-ui, sans-serif;
      background: var(--bg-light);
      color: var(--text-primary);
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
    }
    
    /* ============ HERO SECTION ============ */
    .hero {
      background: var(--primary-gradient);
      color: white;
      padding: 80px 40px;
      text-align: center;
      position: relative;
      overflow: hidden;
    }
    
    .hero::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
      opacity: 0.3;
    }
    
    .hero-content {
      position: relative;
      z-index: 1;
      max-width: 900px;
      margin: 0 auto;
    }
    
    .hero-badge {
      display: inline-block;
      background: rgba(255,255,255,0.15);
      backdrop-filter: blur(10px);
      padding: 8px 20px;
      border-radius: 50px;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      margin-bottom: 24px;
    }
    
    .hero-name {
      font-size: 24px;
      font-weight: 500;
      opacity: 0.9;
      margin-bottom: 8px;
    }
    
    .hero-period {
      font-size: 16px;
      opacity: 0.7;
      margin-bottom: 40px;
    }
    
    .hero-revenue {
      font-size: 72px;
      font-weight: 900;
      letter-spacing: -2px;
      margin-bottom: 16px;
      text-shadow: 0 4px 20px rgba(0,0,0,0.2);
    }
    
    .hero-revenue-label {
      font-size: 18px;
      font-weight: 500;
      opacity: 0.8;
      margin-bottom: 48px;
    }
    
    .hero-stats {
      display: flex;
      justify-content: center;
      gap: 60px;
      flex-wrap: wrap;
    }
    
    .hero-stat {
      text-align: center;
    }
    
    .hero-stat-value {
      font-size: 36px;
      font-weight: 800;
      margin-bottom: 4px;
    }
    
    .hero-stat-label {
      font-size: 13px;
      text-transform: uppercase;
      letter-spacing: 1px;
      opacity: 0.7;
    }
    
    .hero-growth {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: rgba(16, 185, 129, 0.2);
      padding: 10px 24px;
      border-radius: 50px;
      font-size: 16px;
      font-weight: 700;
      margin-top: 32px;
    }
    
    .hero-growth.negative {
      background: rgba(239, 68, 68, 0.2);
    }
    
    /* ============ CONTAINER ============ */
    .container {
      max-width: 1000px;
      margin: 0 auto;
      padding: 60px 40px;
    }
    
    /* ============ HIGHLIGHTS SECTION ============ */
    .section-title {
      font-size: 28px;
      font-weight: 800;
      color: var(--text-primary);
      margin-bottom: 32px;
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .section-title::before {
      content: '';
      display: block;
      width: 4px;
      height: 28px;
      background: var(--primary);
      border-radius: 2px;
    }
    
    .highlights-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;
      margin-bottom: 60px;
    }
    
    .highlight-card {
      background: var(--bg-card);
      border-radius: 20px;
      padding: 32px;
      border: 1px solid var(--border);
      box-shadow: 0 4px 20px rgba(0,0,0,0.03);
      transition: transform 0.2s, box-shadow 0.2s;
    }
    
    .highlight-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(0,0,0,0.08);
    }
    
    .highlight-emoji {
      font-size: 32px;
      margin-bottom: 16px;
    }
    
    .highlight-label {
      font-size: 14px;
      font-weight: 600;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 8px;
    }
    
    .highlight-value {
      font-size: 28px;
      font-weight: 800;
      color: var(--text-primary);
      margin-bottom: 4px;
    }
    
    .highlight-meta {
      font-size: 14px;
      color: var(--text-secondary);
    }
    
    .highlight-card.success .highlight-value { color: var(--success); }
    .highlight-card.warning .highlight-value { color: var(--warning); }
    .highlight-card.danger .highlight-value { color: var(--danger); }
    
    /* ============ INSIGHTS SECTION ============ */
    .insights-section {
      background: var(--bg-card);
      border-radius: 20px;
      padding: 40px;
      margin-bottom: 60px;
      border: 1px solid var(--border);
    }
    
    .insights-title {
      font-size: 20px;
      font-weight: 700;
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    .insight-item {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      padding: 16px 0;
      border-bottom: 1px solid var(--border);
    }
    
    .insight-item:last-child {
      border-bottom: none;
    }
    
    .insight-icon {
      width: 40px;
      height: 40px;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      flex-shrink: 0;
    }
    
    .insight-icon.positive { background: #dcfce7; }
    .insight-icon.negative { background: #fee2e2; }
    .insight-icon.neutral { background: #fef3c7; }
    
    .insight-text {
      font-size: 16px;
      color: var(--text-primary);
      line-height: 1.6;
    }
    
    /* ============ TABLES SECTION ============ */
    .tables-section {
      margin-top: 60px;
    }
    
    .table-card {
      background: var(--bg-card);
      border-radius: 20px;
      overflow: hidden;
      margin-bottom: 40px;
      border: 1px solid var(--border);
    }
    
    .table-header {
      padding: 24px 32px;
      border-bottom: 1px solid var(--border);
    }
    
    .table-title {
      font-size: 18px;
      font-weight: 700;
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
    }
    
    th {
      text-align: left;
      padding: 16px 24px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--text-muted);
      background: var(--bg-light);
    }
    
    td {
      padding: 20px 24px;
      font-size: 14px;
      border-bottom: 1px solid var(--border);
    }
    
    tr:last-child td {
      border-bottom: none;
    }
    
    tr:hover td {
      background: var(--bg-light);
    }
    
    .td-bold {
      font-weight: 600;
    }
    
    .td-success { color: var(--success); font-weight: 600; }
    .td-danger { color: var(--danger); font-weight: 600; }
    
    /* ============ FOOTER ============ */
    .report-footer {
      text-align: center;
      padding: 40px;
      color: var(--text-muted);
      font-size: 14px;
      border-top: 1px solid var(--border);
      margin-top: 40px;
    }
    
    .footer-logo {
      font-size: 20px;
      font-weight: 800;
      color: var(--primary);
      margin-bottom: 8px;
    }
    
    /* ============ PRINT STYLES ============ */
    @media print {
      body { background: white; }
      .hero { 
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        page-break-after: avoid;
      }
      .highlight-card, .table-card, .insights-section {
        break-inside: avoid;
      }
      .container { padding: 40px 20px; }
    }
    
    @page {
      size: A4;
      margin: 0;
    }
    
    /* ============ RESPONSIVE ============ */
    @media (max-width: 768px) {
      .hero { padding: 60px 24px; }
      .hero-revenue { font-size: 48px; }
      .hero-stats { gap: 32px; }
      .hero-stat-value { font-size: 28px; }
      .highlights-grid { grid-template-columns: 1fr; }
      .container { padding: 40px 24px; }
    }
  </style>
</head>
<body>
  <!-- HERO SECTION -->
  <section class="hero">
    <div class="hero-content">
      <div class="hero-badge">📊 Relatório de Performance</div>
      <h1 class="hero-name">${data.userName}</h1>
      <p class="hero-period">${data.period.start} — ${data.period.end}</p>
      
      <div class="hero-revenue">${formatCurrency(data.financial?.confirmedRevenue || 0)}</div>
      <p class="hero-revenue-label">Receita Total Confirmada</p>
      
      <div class="hero-stats">
        <div class="hero-stat">
          <div class="hero-stat-value">${data.financial?.totalHours || 0}h</div>
          <div class="hero-stat-label">Total de Horas</div>
        </div>
        <div class="hero-stat">
          <div class="hero-stat-value">${formatCurrency(data.financial?.realHourlyRate || 0)}/h</div>
          <div class="hero-stat-label">Valor/Hora Real</div>
        </div>
        <div class="hero-stat">
          <div class="hero-stat-value">${data.financial?.billablePercentage || 0}%</div>
          <div class="hero-stat-label">Horas Faturáveis</div>
        </div>
      </div>
      
      ${growthPercent !== null ? `
        <div class="hero-growth ${growthPercent < 0 ? 'negative' : ''}">
          ${growthPercent >= 0 ? '📈' : '📉'} ${growthPercent >= 0 ? '+' : ''}${Math.round(growthPercent)}% vs período anterior
        </div>
      ` : ''}
    </div>
  </section>

  <div class="container">
    <!-- HIGHLIGHTS SECTION -->
    <h2 class="section-title">Destaques do Período</h2>
    <div class="highlights-grid">
      ${topClient ? `
        <div class="highlight-card">
          <div class="highlight-emoji">🏆</div>
          <div class="highlight-label">Cliente Mais Lucrativo</div>
          <div class="highlight-value">${topClient.name}</div>
          <div class="highlight-meta">${formatCurrency(topClient.totalRevenue)} (${topClient.revenueShare}% da receita)</div>
        </div>
      ` : ''}
      
      ${topProject ? `
        <div class="highlight-card success">
          <div class="highlight-emoji">💎</div>
          <div class="highlight-label">Projeto Mais Rentável</div>
          <div class="highlight-value">${topProject.name}</div>
          <div class="highlight-meta">${formatCurrency(topProject.effectiveRate)}/hora efetiva</div>
        </div>
      ` : ''}
      
      <div class="highlight-card ${data.financial?.billablePercentage && data.financial.billablePercentage >= 70 ? 'success' : 'warning'}">
        <div class="highlight-emoji">⏱️</div>
        <div class="highlight-label">Eficiência do Tempo</div>
        <div class="highlight-value">${data.financial?.billablePercentage || 0}%</div>
        <div class="highlight-meta">${data.financial?.billableHours || 0}h faturáveis de ${data.financial?.totalHours || 0}h total</div>
      </div>
      
      ${lostPotential > 0 ? `
        <div class="highlight-card danger">
          <div class="highlight-emoji">💸</div>
          <div class="highlight-label">Potencial Não Faturado</div>
          <div class="highlight-value">${formatCurrency(lostPotential)}</div>
          <div class="highlight-meta">${data.timeReport?.nonBillableHours || 0}h em atividades não faturáveis</div>
        </div>
      ` : `
        <div class="highlight-card success">
          <div class="highlight-emoji">✨</div>
          <div class="highlight-label">Aproveitamento</div>
          <div class="highlight-value">Excelente</div>
          <div class="highlight-meta">Todas as horas foram faturáveis</div>
        </div>
      `}
    </div>

    <!-- INSIGHTS SECTION -->
    ${insights.length > 0 ? `
      <div class="insights-section">
        <h3 class="insights-title">🧠 Análise Automática</h3>
        ${insights.map(insight => `
          <div class="insight-item">
            <div class="insight-icon ${insight.type}">
              ${insight.type === 'positive' ? '✓' : insight.type === 'negative' ? '!' : '→'}
            </div>
            <p class="insight-text">${insight.text}</p>
          </div>
        `).join('')}
      </div>
    ` : ''}

    <!-- TABLES SECTION -->
    <div class="tables-section">
      ${data.projects && data.projects.length > 0 ? `
        <div class="table-card">
          <div class="table-header">
            <h3 class="table-title">📁 Projetos</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Projeto</th>
                <th>Cliente</th>
                <th>Tipo</th>
                <th>Horas</th>
                <th>Receita</th>
                <th>Taxa Efetiva</th>
              </tr>
            </thead>
            <tbody>
              ${data.projects.map(p => `
                <tr>
                  <td class="td-bold">${p.name}</td>
                  <td>${p.client}</td>
                  <td>${p.billingType === 'fixed' ? 'Fixo' : 'Hora'}</td>
                  <td>${p.hoursSpent}h</td>
                  <td>${formatCurrency(p.revenue)}</td>
                  <td class="${p.isProfitable ? 'td-success' : 'td-danger'}">${formatCurrency(p.effectiveRate)}/h</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      ` : ''}

      ${data.clients && data.clients.length > 0 ? `
        <div class="table-card">
          <div class="table-header">
            <h3 class="table-title">👥 Clientes</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Projetos</th>
                <th>Horas</th>
                <th>Receita</th>
                <th>% da Receita</th>
              </tr>
            </thead>
            <tbody>
              ${data.clients.map(c => `
                <tr>
                  <td class="td-bold">${c.name}</td>
                  <td>${c.projectCount}</td>
                  <td>${c.totalHours}h</td>
                  <td>${formatCurrency(c.totalRevenue)}</td>
                  <td class="${c.isHighDependency ? 'td-danger' : ''}">${c.revenueShare}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      ` : ''}

      ${data.financial ? `
        <div class="table-card">
          <div class="table-header">
            <h3 class="table-title">💰 Resumo Financeiro</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Métrica</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Receita Confirmada</td>
                <td class="td-bold td-success">${formatCurrency(data.financial.confirmedRevenue)}</td>
              </tr>
              <tr>
                <td>Receita Pendente</td>
                <td class="td-bold">${formatCurrency(data.financial.expectedRevenue)}</td>
              </tr>
              <tr>
                <td>Receita Total</td>
                <td class="td-bold">${formatCurrency(data.financial.totalRevenue)}</td>
              </tr>
              <tr>
                <td>Total de Horas</td>
                <td class="td-bold">${data.financial.totalHours}h</td>
              </tr>
              <tr>
                <td>Horas Faturáveis</td>
                <td class="td-bold">${data.financial.billableHours}h (${data.financial.billablePercentage}%)</td>
              </tr>
              <tr>
                <td>Valor/Hora Real</td>
                <td class="td-bold">${formatCurrency(data.financial.realHourlyRate)}/h</td>
              </tr>
            </tbody>
          </table>
        </div>
      ` : ''}
    </div>
  </div>

  <!-- FOOTER -->
  <footer class="report-footer">
    <div class="footer-logo">FreelaDash</div>
    <p>Relatório gerado em ${format(new Date(), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}</p>
    <p style="margin-top: 8px; opacity: 0.7;">Sistema de Inteligência Financeira para Freelancers</p>
  </footer>
</body>
</html>`;
}
